import { useEffect, useRef, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  ENGLISH_LANG_CODE,
  buildDefaultForm,
  formToPayload,
  getLanguageLabel,
  resolveImageUrl,
  rowToForm,
} from './offlineMerchantConstants';

export default function OfflineMerchantEditModal({
  open,
  row,
  detail,
  languages,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = Boolean(row?.id);
  const [form, setForm] = useState(() => buildDefaultForm(languages));
  const [error, setError] = useState('');
  const [uploadingLang, setUploadingLang] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (open) {
      setForm(rowToForm(detail || row, languages));
      setError('');
    }
  }, [open, row?.id, detail, languages]);

  if (!open) {
    return null;
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateDetail(lang, key, value) {
    setForm((prev) => ({
      ...prev,
      details: prev.details.map((item) =>
        item.lang === lang ? { ...item, [key]: value } : item
      ),
    }));
  }

  function resetForm() {
    setForm(rowToForm(detail || row, languages));
    setError('');
  }

  function triggerUpload(langCode) {
    fileInputRefs.current[langCode]?.click();
  }

  async function handleImageSelected(langCode, event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setUploadingLang(langCode);
    setError('');
    try {
      const url = await adminApi.uploadOssImage(file);
      updateDetail(langCode, 'image', url || '');
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploadingLang(null);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (form.sort === '' || form.sort == null) {
      setError('請填寫充值順序');
      return;
    }
    if (!form.merchantInterface?.trim()) {
      setError('請填寫商戶接口');
      return;
    }
    const englishDetail = form.details?.find((d) => d.lang === ENGLISH_LANG_CODE);
    if (!englishDetail?.displayName?.trim()) {
      setError('請填寫英語前台顯示');
      return;
    }
    setError('');
    await onSubmit(formToPayload(form, row?.id, languages));
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{isEdit ? '修改' : '新增線下商戶'}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            <section className="settings-section">
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="offlineMerchantSort">
                    <span className="required-mark">*</span> 充值順序
                  </label>
                  <input
                    id="offlineMerchantSort"
                    type="number"
                    value={form.sort}
                    onChange={(e) => updateField('sort', e.target.value)}
                  />
                </div>
                <div className="form-field form-field--full">
                  <label htmlFor="offlineMerchantInterface">
                    <span className="required-mark">*</span> 商戶接口
                  </label>
                  <input
                    id="offlineMerchantInterface"
                    type="text"
                    value={form.merchantInterface}
                    onChange={(e) => updateField('merchantInterface', e.target.value)}
                    placeholder="如 UD_PAYMENT"
                  />
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4>前台顯示</h4>
              <div className="form-grid">
                {(languages || []).map((language) => {
                  const detailRow = form.details.find((d) => d.lang === language.langCode) || {};
                  const label = getLanguageLabel(language);
                  const required = language.langCode === ENGLISH_LANG_CODE;
                  return (
                    <div key={`display-${language.langCode}`} className="form-field form-field--full">
                      <label htmlFor={`offlineDisplayName-${language.langCode}`}>
                        {required && <span className="required-mark">*</span>}
                        前台顯示（{label}）
                      </label>
                      <input
                        id={`offlineDisplayName-${language.langCode}`}
                        type="text"
                        value={detailRow.displayName || ''}
                        onChange={(e) =>
                          updateDetail(language.langCode, 'displayName', e.target.value)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="settings-section">
              <h4>圖示</h4>
              <div className="offline-merchant-images">
                {(languages || []).map((language) => {
                  const detailRow = form.details.find((d) => d.lang === language.langCode) || {};
                  const label = getLanguageLabel(language);
                  const imageUrl = resolveImageUrl(detailRow.image);
                  return (
                    <div key={`image-${language.langCode}`} className="offline-merchant-image-item">
                      <label>圖示（{label}）</label>
                      <div className="offline-merchant-image-preview">
                        {imageUrl ? (
                          <img src={imageUrl} alt={`${label} icon`} />
                        ) : (
                          <span className="offline-merchant-image-placeholder">尚未上傳</span>
                        )}
                      </div>
                      <input
                        ref={(node) => {
                          fileInputRefs.current[language.langCode] = node;
                        }}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleImageSelected(language.langCode, e)}
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={uploadingLang === language.langCode || submitting}
                        onClick={() => triggerUpload(language.langCode)}
                      >
                        {uploadingLang === language.langCode
                          ? '上傳中…'
                          : `上傳圖片${label}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>

            {error && <div className="alert alert-error">{error}</div>}
          </div>
          <div className="admin-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
            <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={submitting}>
              重置
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
